import { useState } from 'react';
import { ChevronDown, ChevronRight, Lightbulb } from 'lucide-react';
import { EngineerHead } from '@/features/normas/Engineer';

export function AnalysisExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <section
      className="rounded-[var(--radius-md)] border"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <div
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
          style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--accent)' }}
        >
          <Lightbulb size={14} strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Acerca del analisis
          </div>
          <div className="text-sm" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            ¿Que significa este diagrama 3D?
          </div>
        </div>
        {open ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
      </button>

      {open && (
        <div className="border-t px-4 pb-5 pt-4" style={{ borderColor: 'var(--border-subtle)' }}>
          <Bubble>
            <p>
              <b>El solido P-Mx-My</b> es la <b>superficie envolvente de capacidad</b> de la columna.
              Cada punto sobre el solido representa una combinacion (P, Mx, My) que la columna puede
              resistir <i>justo en el limite</i>, con el factor de reduccion phi de E.060 ya aplicado.
            </p>
          </Bubble>

          <Step n={1} title="Como se construye">
            Con los materiales y el armado dados, fijo la inclinacion del eje neutro, integro las
            fuerzas del concreto (bloque de Whitney) y del acero (elastoplastico), y obtengo un
            punto (P, Mx, My) por cada profundidad de eje neutro. Repito para 24 inclinaciones que
            barren los 360°: la union forma la malla violeta que ves.
          </Step>

          <Step n={2} title="Como se interpreta">
            La <b>esfera amber</b> es tu punto de demanda <code>(Pu, Mu_x, Mu_y)</code>. Si esta
            <b> dentro</b> del solido, la columna pasa. Si esta <b>fuera</b>, no pasa: hay que
            aumentar As, agrandar la seccion o cambiar el material.
          </Step>

          <Step n={3} title="Por que la forma cambia">
            La forma del solido depende de:
            <ul className="ml-5 mt-1 list-disc space-y-0.5 text-[12px]">
              <li>la <b>geometria</b> (L1, L2): mas concreto -&gt; mas capacidad axial.</li>
              <li>el <b>armado</b> (cantidad y diametro): mas acero -&gt; mas capacidad a flexion.</li>
              <li>la <b>resistencia</b> (f'c, fy): mejores materiales -&gt; envolvente mas grande.</li>
            </ul>
            Modifica esos inputs y veras el solido inflarse o achicarse en vivo.
          </Step>

          <Step n={4} title="Para que sirve">
            Verificas <b>flexocompresion biaxial</b> en una sola pasada. Sin esto, tendrias que
            chequear independientemente las dos direcciones (P-Mx y P-My) y luego aplicar formulas
            de interaccion (Bresler, contornos de carga). El solido es la verificacion exacta.
          </Step>

          <Step n={5} title="Los cortes 2D abajo">
            Son los planos donde haces zoom: <code>P-M22</code> es el corte con My = 0 (sismo X
            puro), <code>P-M33</code> es con Mx = 0 (sismo Y puro). El punto amber es la
            proyeccion de tu demanda sobre ese plano.
          </Step>
        </div>
      )}
    </section>
  );
}

function Bubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-start gap-2.5">
      <div className="shrink-0">
        <EngineerHead size={36} />
      </div>
      <div
        className="relative flex-1 rounded-[var(--radius-sm)] border px-3 py-2 text-[12px]"
        style={{
          background: 'var(--surface-2)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}
      >
        <span
          aria-hidden
          className="absolute -left-1.5 top-3 h-3 w-3 rotate-45 border-b border-l"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
        />
        {children}
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center gap-2">
        <span
          className="flex h-5 w-5 items-center justify-center rounded-full font-mono tabular text-[10px]"
          style={{ background: 'var(--surface-3)', color: 'var(--accent)' }}
        >
          {n}
        </span>
        <span className="text-[12px]" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
          {title}
        </span>
      </div>
      <div className="pl-7 text-[12px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}
